/*
 * Greeting card messages.
 *
 * A card's printed message rides on its basket line as the line item property
 * "Card message" (see snippets/product-personalisation.liquid and the drawer's
 * Add Extras panel). This file holds:
 *
 * - window.B22CardMessage.stripEmoji(input): the card printer can't print emoji,
 *   so every message field removes them as the customer types (with a visible
 *   hint next to the field, so nothing disappears unexplained).
 *
 * - <b22-card-message>: the "Edit message" control on a card's basket line, in
 *   the drawer and on /cart. Saving rewrites the property in place through
 *   /cart/change.js (quantity stays 1) and re-renders the same sections cart.js
 *   does for a quantity change, so the drawer never has to remove and re-add.
 */
(function () {
  const EMOJI = /[\p{Extended_Pictographic}\u{1F1E6}-\u{1F1FF}\u{1F3FB}-\u{1F3FF}\u200D\uFE0F]/gu;
  const HAS_EMOJI = /[\p{Extended_Pictographic}\u{1F1E6}-\u{1F1FF}\u{1F3FB}-\u{1F3FF}\u200D\uFE0F]/u;

  window.B22CardMessage = {
    stripEmoji(input) {
      if (!HAS_EMOJI.test(input.value)) return;
      const pos = input.selectionStart;
      const before = input.value.length;
      input.value = input.value.replace(EMOJI, '').replace(/ {2,}/g, ' ');
      const caret = Math.max(0, pos - (before - input.value.length));
      input.setSelectionRange(caret, caret);
    },
  };

  if (customElements.get('b22-card-message')) return;

  customElements.define(
    'b22-card-message',
    class B22CardMessageLine extends HTMLElement {
      connectedCallback() {
        this.view = this.querySelector('[data-card-view]');
        this.editor = this.querySelector('[data-card-editor]');
        this.input = this.querySelector('[data-card-input]');
        this.counter = this.querySelector('[data-card-counter]');
        this.error = this.querySelector('[data-card-error]');

        this.addEventListener('click', (event) => {
          if (event.target.closest('[data-card-edit]')) this.open();
          if (event.target.closest('[data-card-cancel]')) this.close();
          if (event.target.closest('[data-card-save]')) this.save();
        });
        this.input.addEventListener('input', () => this.onInput());
        // cart.js treats any "change" inside <cart-items> as a quantity edit.
        this.addEventListener('change', (event) => event.stopPropagation());
      }

      open() {
        this.view.hidden = true;
        this.editor.hidden = false;
        this.onInput();
        this.input.focus();
        this.input.setSelectionRange(this.input.value.length, this.input.value.length);
      }

      close() {
        this.input.value = this.dataset.message;
        this.error.hidden = true;
        this.editor.hidden = true;
        this.view.hidden = false;
      }

      onInput() {
        window.B22CardMessage.stripEmoji(this.input);
        this.counter.textContent = `${this.input.value.length} / ${this.input.maxLength}`;
        if (this.input.value.trim() !== '') this.error.hidden = true;
      }

      save() {
        const message = this.input.value.replace(/ {2,}/g, ' ').trim();
        if (message === '') {
          this.error.hidden = false;
          this.input.focus();
          return;
        }
        if (message === this.dataset.message) {
          this.close();
          return;
        }

        const cartItems = this.closest('cart-items') || document.querySelector('cart-items');
        const sections = cartItems.cartLocation.getSectionsToRender();
        const cartUrl = routes.cart_add_url.replace(/\/add(\.js)?$/, '');

        cartItems.enableLoading(this.dataset.line);
        // Keep every other property on the line (hidden "_" ones included), read
        // from the live cart because Liquid can't serialise them as an object.
        fetch(`${cartUrl}.js`)
          .then((response) => response.json())
          .then((cart) => {
            const line = cart.items.find((item) => item.key === this.dataset.key);
            const properties = Object.assign({}, line && line.properties, { 'Card message': message });
            return fetch(`${routes.cart_change_url}`, {
              ...fetchConfig(),
              body: JSON.stringify({
                id: this.dataset.key,
                quantity: 1,
                properties,
                sections: sections.map((section) => section.section),
                sections_url: window.location.pathname,
              }),
            });
          })
          .then((response) => response.json())
          .then((state) => {
            if (state.errors || state.status) throw new Error(state.errors || state.description);
            sections.forEach((section) => {
              const container = document.getElementById(section.id);
              if (!container) return;
              const target = container.querySelector(section.selector) || container;
              target.innerHTML = cartItems.getSectionInnerHTML(state.sections[section.section], section.selector);
            });
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'card-message', cartData: state });
          })
          .catch((error) => {
            console.error('card message update failed:', error);
            this.error.textContent = "Sorry, we couldn't save that. Please try again.";
            this.error.hidden = false;
          })
          .finally(() => cartItems.disableLoading());
      }
    }
  );
})();
